import {Injectable} from '@angular/core';
import {User} from './user.model';
import {JwtHelperService} from '@auth0/angular-jwt';
import {Publisher} from '../../shared/domain/publisher.model';
import {UserRole} from './user-role.model';
import {Router, UrlTree} from '@angular/router';
import {PrincipalUtils} from './principal.utils';
import {PrincipalMask} from './principal-mask.model';

@Injectable({providedIn: 'root'})
export class Principal {

  userFromToken: User;
  public user: User;
  public publisher: Publisher;
  accessToken: string;

  mask: PrincipalMask = new PrincipalMask();

  constructor(private router: Router) {
  }

  setToken(token: string | null) {
    if (!token) {
      this.userFromToken = null;
      this.accessToken = null;
      return;
    }
    try {
      this.userFromToken = new JwtHelperService().decodeToken(token);
    } catch (e) {
      console.error('user could not be extracted from token');
    }
    this.accessToken = token;
  }

  getToken() {
    return this.accessToken;
  }

  cleanup() {
    console.log('principal cleanup');
    this.userFromToken = null;
    this.user = null;
    this.publisher = null;
    this.accessToken = null;
    this.setMask();
  }

  setUser(user: User) {
    this.user = user;
    console.log('setUser: %o', user);
    if (user) {
      const mask = localStorage.getItem('mask');
      if (user.network?.id == '3') {
        window.document.getElementById('appFavicon').setAttribute('href', 'assets/favicons/favicon-3.png');
        window.document.title = 'CTVBuyer';
      }
      if (mask) {
        console.log('mask found in local cache %s', mask);
        this.mask = JSON.parse(mask);
        this.validateMask();
      } else {
        console.log('no mask in local cache');
      }
    }
  }

  getUser() {
    return this.user;
  }

  hasAuthority(authority: string) {
    return this.hasAnyAuthority([authority]);
  }

  hasAnyAuthority(authorities: string[]) {
    if (!authorities || !this.user || !this.user.roles) {
      return false;
    }

    for (let i = 0; i < authorities.length; i++) {
      if (this.user.roles.indexOf(authorities[i]) !== -1) {
        return true;
      }
    }

    return false;
  }

  isAuthenticated() {
    return this.getToken() != null;
  }

  canAccess(app: 'admin' | 'publisher' | 'master' | 'co' | 'sr' | 'cm' | 'csm' ): boolean | UrlTree {
    if (!this.hasAnyAuthority(PrincipalUtils.MAIN_ROLES) && !this.hasAnyAuthority(PrincipalUtils.ADDITIONAL_ROLES)) {
      // canAccess() re-runs on every route activation (AdminGuard etc.), so a blocking alert()
      // here would refire on every navigation attempt instead of showing once. Redirect to the
      // existing /forbidden route instead - same pattern already used by NetworkGuard and
      // NetworkGuardPublisher for other access-denied cases.
      return this.router.createUrlTree(['/forbidden']);
    }

    if (!PrincipalUtils.isMasked(this)) {
      this.tryToSetDefaultMask();
    }

    //All Main Roles using main dash
    if (PrincipalUtils.MAIN_ROLES.includes(this.mask.role)) {
      return true;
    }

    if (PrincipalUtils.APP_MAP[app] !== this.mask.role) {
      return this.router.createUrlTree([this.getRedirectPath()]);
    }

    return true;
  }

  setMask(role?: UserRole, id?) {
    this.mask = new PrincipalMask();
    this.mask.role = role;
    if (role === UserRole.PUBLISHER) {
      this.mask.publisherId = id;
    }
    if (role === UserRole.CONTENT_OWNER) {
      this.mask.contentOwnerId = id;
    }
    localStorage.setItem('mask', JSON.stringify(this.mask));
    console.log('setMask=%s', JSON.stringify(this.mask));
  }

  private tryToSetDefaultMask() {
    console.log('tryToSetDefaultMask');
    // const mainRoles = PrincipalUtils.filterMainRoles(this.user.roles);

    // set defaults
    if (this.user.roles.length === 1) {
      const maskRole = this.user.roles[0] as UserRole;
      switch (maskRole) {
        case UserRole.PUBLISHER:
          if (this.user.publisherIds.length === 1) {
            this.setMask(maskRole, this.user.publisherIds[0]);
          }
          break;
        case UserRole.ADMIN:
          this.setMask(maskRole, null);
          break;
        case UserRole.CONTENT_OWNER:
          if (this.user.contentOwnerIds.length === 1) {
            this.setMask(maskRole, this.user.contentOwnerIds[0]);
          }
          break;
        case UserRole.SALES_REPRESENTATIVE:
          this.setMask(maskRole, null);
          break;
        case UserRole.READ_ONLY_ADMIN:
          this.setMask(maskRole, null);
          break;
        case UserRole.SUPER_ADMIN:
        case UserRole.USER:
        case UserRole.READONLY_USER:
        case UserRole.CAMPAIGN_MANAGER:
        case UserRole.REV_OPS:
        case UserRole.DESIGNER:
        case UserRole.ADVERTISER:
        case UserRole.FINANCE:
          this.setMask(maskRole, null);
          break;
      }
    }
  }

  /** Ensures a default mask is applied when the user has a single main role. */
  ensureDefaultMask() {
    if (!PrincipalUtils.isMasked(this)) {
      this.tryToSetDefaultMask();
    }
  }

  getRedirectPath() {
    if (!PrincipalUtils.isMasked(this)) {
      return '/select';
    }

    switch (this.mask.role) {
      case UserRole.ADMIN:
        return '/';
      case UserRole.PUBLISHER:
        return `/p/${this.mask.publisherId}`;
      case UserRole.CONTENT_OWNER:
        return `/co/${this.mask.contentOwnerId}`;
      case UserRole.SALES_REPRESENTATIVE:
        return `/`;
      case UserRole.READ_ONLY_ADMIN:
        return `/`;
      case UserRole.SUPER_ADMIN:
        return '/';
      case UserRole.USER:
      case UserRole.READONLY_USER:
        return `/dashboard`;
      case UserRole.CAMPAIGN_MANAGER:
        return `/orders`;
      case UserRole.REV_OPS:
        return `/forecast`;
      case UserRole.DESIGNER:
        return `/campaigns/creative-library`;
      case UserRole.ADVERTISER:
      case UserRole.FINANCE:
        return `/reports`;
    }
  }

  private validateMask() {
    const roleOk = this.user.roles.includes(this.mask.role);
    const pubOk = !this.mask.publisherId || this.user.publisherIds && this.user.publisherIds.includes(this.mask.publisherId);
    if (!roleOk || !pubOk) {
      this.mask = new PrincipalMask();
    }
  }
}
